
import { textSearch } from "@/assets/ts/_utils";
import { toLocaleDateString } from "@/assets/ts/_utils/formatters";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { useAdminComarca } from "@/composable/useAdminComarca";
import { edadesProfesionales } from "@/core/data/edadesProfesionales";
import { generos } from "@/core/data/generos";
import { municipiosComarcas } from "@/core/data/municipiosProvinciasComarcas";
import { titulacionesTodas } from "@/core/data/titulaciones";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { SortProps, sortFn } from "@/core/helpers/sort";
import { FormularioEstado } from "@/enums";
import Page from "@/layout/Page.vue";
import {
  AuthModule,
  OrganismosModule,
  ProfesionalesFormulariosModule,
} from "@/store/modules";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { useField } from "vee-validate";
import {
  computed,
  defineComponent,
  onActivated,
  onMounted,
  ref,
  watch,
} from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export default defineComponent({
  name: "formularios-profesionales-list",
  components: { "download-excel": JsonExcel, Page },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const module = getModule(ProfesionalesFormulariosModule, store);
    const authModule = getModule(AuthModule, store);
    const comarcaId = authModule.user.comarcaId;

    const organismosModule = getModule(OrganismosModule, store);
    const { selectedComarcaId } = useAdminComarca();
    const loadingComarcas = ref(false);

    const ensureComarcasLoaded = async () => {
      if (organismosModule.organismosListado.length === 0) {
        loadingComarcas.value = true;
        try {
          await organismosModule.fetchAll();
        } finally {
          loadingComarcas.value = false;
        }
      }
    };

    const comarcas = computed(() => {
      const list = organismosModule.organismosListado.map((item) => ({
        id: item.id,
        nombre: item.nombre,
      }));
      list.unshift({ id: "TODAS", nombre: "Todas" });
      return list;
    });

    const csvLabels = {
      nombreComarca: t("csv.solicitudes.familias.comarca"),
      codigo: t("csv.solicitudes.profesionales.codigo"),
      createdOnCsv: t("csv.solicitudes.profesionales.createdOn"),
      dni: t("csv.solicitudes.profesionales.dni"),
      nombre: t("csv.listados.profesionales.nombre"),
      apellidos: t("csv.listados.profesionales.apellidos"),
      telefono: t("csv.listados.profesionales.telefono"),
      email: t("csv.listados.profesionales.email"),
      estado: t("csv.solicitudes.profesionales.estado"),
      fechaNacimientoCsv: t("csv.listados.profesionales.fechaNacimiento"),
      direccion: t("csv.listados.profesionales.direccion"),
      localidad: t("csv.listados.profesionales.localidad"),
      provincia: t("csv.listados.profesionales.provincia"),
      titulacionText: t("csv.solicitudes.profesionales.titulacion"),
      titulacionOtras: t("csv.solicitudes.profesionales.titulacionOtras"),
      carnetConducir: t("csv.listados.profesionales.carnetConducir"),
      vehiculo: t("csv.listados.profesionales.vehiculo"),
    };
    const csvFields = [
      ...(!comarcaId ? ["nombreComarca"] : []),
      "codigo",
      "createdOnCsv",
      "dni",
      "nombre",
      "apellidos",
      "telefono",
      "email",
      "fechaNacimientoCsv",
      "direccion",
      "localidad",
      "provincia",
      "titulacionText",
      "titulacionOtras",
      "carnetConducir",
      "vehiculo",
      "estado",
    ];
    const csvName = "profesionales" + new Date().getTime() + ".xlsx";

    const search = ref("");

    const error = computed(() => module.errors);

    const estado = ref<string | null>(FormularioEstado.pendiente); // valor por defecto "Pendiente"

    watch(error, () => {
      if (!error.value) return;

      Swal.fire({
        text:
          t("errors." + (error.value.error.code ?? error.value.error.name)) ??
          error.value.error.name,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: t("dialogs.okButton"),
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    });

    const cargaListado = ref(false);

    const fetchData = async () => {
      const isAdmin = !comarcaId;
      if (isAdmin && !selectedComarcaId.value) {
        module.setData([]);
        cargaListado.value = false;
        search.value = "";
        currentPage.value = 1;
        return;
      }

      let idToFetch: string | undefined;
      if (isAdmin) {
        idToFetch =
          selectedComarcaId.value === "TODAS"
            ? undefined
            : selectedComarcaId.value!;
      } else {
        idToFetch = comarcaId;
      }

      cargaListado.value = true;
      try {
        await module.fetchFormularios(idToFetch);
        search.value = "";
        currentPage.value = 1;
      } catch (err) {
        Swal.fire({
          text: t("formularios.familiaListado.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaListado.value = false;
      }
    };

    if (!comarcaId) {
      watch(selectedComarcaId, async () => {
        localidad.value = "";
        genero.value = "";
        rangoedad.value = "";
        await fetchData();
      });
    }

    onActivated(async () => {
      if (!comarcaId) {
        ensureComarcasLoaded();
      }
      await fetchData();
    });

    onMounted(() => {
      setCurrentPageBreadcrumbs(t("menu.profesionales"), [
        t("menu.solicitudes"),
      ]);
    });

    const handleEdit = (index, row) => {
      router.push(`profesionales/${row.id}`);
    };

    const profesionales = computed(() => {
      return module.profesionalesFormularios?.map((ele) => {
        let edad: number | null = null;
        if (ele?.datosSolicitud?.fechaNacimiento) {
          const dob = new Date(ele.datosSolicitud?.fechaNacimiento);
          if (!isNaN(dob.getTime())) {
            const today = new Date();
            edad = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
              edad--;
            }
          }
        }
        return {
          ...ele,
          edad,
          nombre: ele.datosSolicitud.nombre,
          apellidos: ele.datosSolicitud.apellidos,
          solicitante: [
            ele.datosSolicitud.nombre,
            ele.datosSolicitud.apellidos,
          ].join(" "),
          telefono: ele.datosSolicitud.telefono,
          email: ele.datosSolicitud.email,
          createdOnCsv: new Date(ele.createdOn),
          fechaNacimientoCsv: new Date(ele.datosSolicitud.fechaNacimiento),
          direccion: ele.datosSolicitud.direccion,
          localidad: ele.datosSolicitud.localidad,
          provincia: ele.datosSolicitud.provincia,
          carnetConducir: ele.datosSolicitud.carnetConducir,
          vehiculo: ele.datosSolicitud.vehiculoPropio,
          titulacion: ele.datosSolicitud.titulacion.map((titulacion) => {
            return (
              titulacionesTodas.find((ele) => ele.id === titulacion)?.text ??
              titulacion
            );
          }),
          titulacionText:
            ele.datosSolicitud.titulacion
              ?.map((t) => titulacionesTodas.filter((a) => a.id == t))
              ?.map((t) => t.map((r) => r.text))
              .join(", ") || "",
          titulacionOtras: ele.datosSolicitud.titulacionOtras,
          nombreComarca: ele.comarca?.nombre,
        };
      });
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const filteredCollection = computed(() => {
      let filteredBySearch = (profesionales.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          const matchesSearch =
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.solicitante, search.value) ||
            textSearch(data.datosSolicitud?.dni, search.value) ||
            textSearch(data.datosSolicitud?.email, search.value) ||
            textSearch(data.datosSolicitud?.telefono, search.value) ||
            textSearch(data.estado, search.value);

          const matchesEstado = !estado.value || data.estado === estado.value;

          return matchesSearch && matchesEstado;
        });

      if (localidad?.value !== undefined && localidad?.value !== "") {
        // Intersección de registros filtrados por búsqueda y por localidad
        filteredBySearch = filteredBySearch?.filter((data) =>
          profesionales.value
            ?.filter((data) => data.localidad == localidad.value)
            ?.includes(data)
        );
      }
      if (genero?.value !== undefined && genero?.value !== "") {
        filteredBySearch = filteredBySearch?.filter((data) =>
          profesionales.value
            ?.filter((data) => data.datosSolicitud.tratamiento == genero.value)
            ?.includes(data)
        );
      }

      if (rangoedad?.value !== undefined && rangoedad?.value !== "") {
        filteredBySearch = filteredBySearch?.filter((data) => {
          const edadActual = data.edad;
          // si no hay fecha válida, no incluir en filtro por edad
          if (edadActual === null || edadActual === undefined) return false;

          if (rangoedad.value === "MENOS30") {
            return edadActual < 30;
          } else if (rangoedad.value === "3045") {
            return edadActual >= 30 && edadActual <= 45;
          } else if (rangoedad.value === "MAS45") {
            return edadActual > 45;
          }
          return true;
        });
      }

      return filteredBySearch;
    });

    const municipiosComarca = computed(() => {
      const isAdmin = !comarcaId;
      if (!isAdmin) {
        return municipiosComarcas.filter(
          (municipio) => municipio.comarcaId === comarcaId
        );
      }
      if (!selectedComarcaId.value || selectedComarcaId.value === "TODAS") {
        return municipiosComarcas;
      }
      return municipiosComarcas.filter(
        (municipio) => municipio.comarcaId === selectedComarcaId.value
      );
    });

    const { value: localidad } = useField("localidad");
    const { value: genero } = useField("genero");
    const { value: rangoedad } = useField("rangoedad");
    const { result, currentPage, lastPage } = useArrayPagination(
      filteredCollection,
      { pageSize: 100 }
    );

    const onComarcaVisibleChange = (open: boolean) => {
      if (open && !comarcaId) {
        ensureComarcasLoaded();
      }
    };

    return {
      profesionalesFormularios: filteredCollection,
      cargaListado,
      isAdmin: !comarcaId,
      csvLabels,
      csvFields,
      csvName,
      handleEdit,
      FormularioEstado,
      search,
      toLocaleDateString,
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange: (page: number) => (currentPage.value = page),
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      estado,
      estados: [
        {
          value: FormularioEstado.pendiente,
          label: t("estados.PENDIENTE"),
        },
        {
          value: FormularioEstado.noApto,
          label: t("estados.NO_APTO"),
        },
        {
          value: FormularioEstado.realizadoPorError,
          label: t("estados.REALIZADO_POR_ERRROR"),
        },
      ],
      municipiosComarca,
      localidad,
      generos,
      genero,
      rangoedad,
      edadesProfesionales,
      selectedComarcaId,
      comarcas,
      loadingComarcas,
      onComarcaVisibleChange,
    };
  },
});
